import React, { useState, useRef } from 'react';
import { sendMergedForEsign } from '../../services/demoApi';

const Step4ESign = ({ formData, onNext, demoState, setDemoState, saveStateForRedirect }) => {
  const [phase, setPhase] = useState(
    demoState.esignStatus === 'cancelled' ? 'cancelled' : 'consent'
  );
  const [agreed, setAgreed] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerMobile, setSignerMobile] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const hasCalledApi = useRef(false);

  // Try to pre-fill from form data (template variables)
  // Look for common variable keys that match signer info
  const prefillFromFormData = () => {
    const fd = formData || {};
    const nameKeys = ['borrower_name', 'first_party_name', 'signer_name', 'name', 'full_name'];
    const mobileKeys = ['contact_number', 'mobile', 'phone', 'signer_mobile', 'mobile_number'];

    if (!signerName) {
      for (const key of nameKeys) {
        if (fd[key]) { setSignerName(fd[key]); break; }
      }
    }
    if (!signerMobile) {
      for (const key of mobileKeys) {
        if (fd[key]) {
          // Strip non-digits and +91 prefix
          const cleaned = fd[key].replace(/\D/g, '').replace(/^91/, '');
          if (cleaned.length === 10) { setSignerMobile(cleaned); break; }
        }
      }
    }
  };

  // Call prefill on first render of consent phase
  useState(() => { prefillFromFormData(); });

  const validateFields = () => {
    const errs = {};
    if (!signerName.trim()) errs.name = 'Signer name is required';
    if (!signerEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signerEmail)) {
      errs.email = 'Valid email address is required';
    }
    if (!signerMobile.trim() || !/^[6-9]\d{9}$/.test(signerMobile)) {
      errs.mobile = 'Valid 10-digit mobile number required';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /**
   * Initiate the real eSign flow:
   * 1. Send merged PDF to Meon via backend
   * 2. Receive esign_url
   * 3. Save state to sessionStorage
   * 4. Redirect user to Meon/NSDL gateway
   */
  const handleInitiateEsign = async () => {
    if (!validateFields()) return;
    if (hasCalledApi.current) return;
    hasCalledApi.current = true;

    setPhase('sending');
    setError('');

    try {
      const mergedBytes = demoState.mergedPdfBytes;
      if (!mergedBytes) {
        throw new Error('Merged PDF not available. Please go back to Step 3.');
      }

      // Persist signer email for the completion email
      setDemoState(prev => ({ ...prev, signerEmail }));

      const result = await sendMergedForEsign(mergedBytes, signerName, signerEmail, signerMobile);

      // Store eSign data
      setDemoState(prev => ({
        ...prev,
        envelopeId: result.envelope_id,
        esignUrl: result.esign_url,
        signerEmail,
      }));

      // If we got an esign_url, redirect user to the Meon gateway
      if (result.esign_url) {
        setPhase('redirecting');

        // Save state AFTER we have the envelope ID
        setTimeout(() => {
          saveStateForRedirect();
          // Small delay so the user sees the "Redirecting..." UI
          setTimeout(() => {
            window.location.href = result.esign_url;
          }, 1000);
        }, 200);
      } else {
        // No URL returned — show the sent state directly (sandbox mode)
        setPhase('signed');
      }
    } catch (err) {
      console.error('eSign send failed:', err);
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      setError(detail);
      setPhase('error');
      hasCalledApi.current = false;
    }
  };

  // ─── Consent Phase ────────────────────────────────────────
  if (phase === 'consent') {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number">4</div>
          <span className="loan-step-text">Step 4 of 5 — Aadhaar eSign</span>
        </div>
        <div className="loan-card">
          <div className="loan-api-tag loan-api-tag--esign">
            🔐 Aadhaar eSign API
          </div>
          <h2 className="loan-heading" style={{ fontSize: 22 }}>
            Sign Your Agreement
          </h2>
          <p className="loan-subheading" style={{ marginBottom: 16 }}>
            Digitally sign your Loan Agreement using Aadhaar-based eSign.
            You will be redirected to the NSDL-certified eSign Gateway to complete the signing.
          </p>

          <div className="loan-info-row">
            <div className="loan-info-row__icon">🏛️</div>
            <div className="loan-info-row__content">
              <div className="loan-info-row__title">Secure Gateway</div>
              <div className="loan-info-row__subtitle">NSDL / CDAC Certified eSign Provider (Meon)</div>
            </div>
          </div>

          <div className="loan-info-row">
            <div className="loan-info-row__icon">📋</div>
            <div className="loan-info-row__content">
              <div className="loan-info-row__title">Document to Sign</div>
              <div className="loan-info-row__subtitle">Stamp Paper + Loan Agreement (Merged PDF)</div>
            </div>
          </div>

          {/* Signer details form */}
          <div style={{ marginTop: 16 }}>
            <p style={{
              fontSize: 11, fontWeight: 600, color: 'var(--loan-text-secondary)',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ width: 12, height: 1, background: 'var(--loan-border)' }} />
              Signer Details
              <span style={{ flex: 1, height: 1, background: 'var(--loan-border)' }} />
            </p>

            <div className="loan-form-group">
              <label className="loan-label" htmlFor="signer-name">
                Full Name <span style={{ color: 'var(--loan-error)' }}>*</span>
              </label>
              <input
                id="signer-name"
                className="loan-input"
                type="text"
                placeholder="e.g. Rajesh Kumar Sharma"
                value={signerName}
                onChange={e => {
                  setSignerName(e.target.value);
                  if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: undefined }));
                }}
                style={fieldErrors.name ? { borderColor: 'var(--loan-error)' } : {}}
              />
              {fieldErrors.name && (
                <span style={{ fontSize: 12, color: 'var(--loan-error)', marginTop: 4, display: 'block' }}>
                  {fieldErrors.name}
                </span>
              )}
            </div>

            <div className="loan-form-group">
              <label className="loan-label" htmlFor="signer-email">
                Email Address <span style={{ color: 'var(--loan-error)' }}>*</span>
              </label>
              <input
                id="signer-email"
                className="loan-input"
                type="email"
                placeholder="e.g. rajesh@example.com"
                value={signerEmail}
                onChange={e => {
                  setSignerEmail(e.target.value);
                  if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: undefined }));
                }}
                style={fieldErrors.email ? { borderColor: 'var(--loan-error)' } : {}}
              />
              {fieldErrors.email && (
                <span style={{ fontSize: 12, color: 'var(--loan-error)', marginTop: 4, display: 'block' }}>
                  {fieldErrors.email}
                </span>
              )}
            </div>

            <div className="loan-form-group">
              <label className="loan-label" htmlFor="signer-mobile">
                Mobile Number <span style={{ color: 'var(--loan-error)' }}>*</span>
              </label>
              <div className="loan-input-prefix">
                <div className="loan-input-addon">+91</div>
                <input
                  id="signer-mobile"
                  className="loan-input"
                  type="tel"
                  maxLength={10}
                  placeholder="98765 43210"
                  value={signerMobile}
                  onChange={e => {
                    setSignerMobile(e.target.value.replace(/\D/g, ''));
                    if (fieldErrors.mobile) setFieldErrors(prev => ({ ...prev, mobile: undefined }));
                  }}
                  style={fieldErrors.mobile ? { borderColor: 'var(--loan-error)' } : {}}
                />
              </div>
              {fieldErrors.mobile && (
                <span style={{ fontSize: 12, color: 'var(--loan-error)', marginTop: 4, display: 'block' }}>
                  {fieldErrors.mobile}
                </span>
              )}
            </div>
          </div>

          <label className="loan-checkbox-row" htmlFor="esign-consent" style={{ marginTop: 16 }}>
            <input
              type="checkbox"
              className="loan-checkbox"
              id="esign-consent"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
            />
            <span className="loan-checkbox-text">
              I hereby authorize NeoBank to use my Aadhaar details for the purpose of
              electronically signing this Loan Agreement document as per IT Act 2000.
            </span>
          </label>

          <div style={{ marginTop: 20 }}>
            <button
              className="loan-btn loan-btn--primary"
              disabled={!agreed || !signerName || !signerEmail || !signerMobile}
              onClick={handleInitiateEsign}
            >
              Proceed to Aadhaar eSign Gateway →
            </button>
          </div>

          <div className="loan-trust-note" style={{ marginTop: 16 }}>
            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: 13, height: 13 }}>
              <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
            </svg>
            You will be redirected to the official NSDL eSign portal
          </div>
        </div>
      </>
    );
  }

  // ─── Sending Phase ────────────────────────────────────────
  if (phase === 'sending') {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number">4</div>
          <span className="loan-step-text">Step 4 of 5 — Aadhaar eSign</span>
        </div>
        <div className="loan-card">
          <div className="loan-api-tag loan-api-tag--esign">
            ⚡ Aadhaar eSign — Sending Document
          </div>
          <div className="loan-processing">
            <div className="loan-spinner" />
            <div className="loan-processing__text">Sending Document to eSign Gateway...</div>
            <div className="loan-processing__subtext">Uploading merged PDF to Meon → Preparing signing session</div>
          </div>
        </div>
      </>
    );
  }

  // ─── Redirecting Phase ────────────────────────────────────
  if (phase === 'redirecting') {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number">4</div>
          <span className="loan-step-text">Step 4 of 5 — Aadhaar eSign</span>
        </div>
        <div className="loan-card">
          <div className="loan-api-tag loan-api-tag--esign">
            🔀 Redirecting to NSDL Gateway
          </div>
          <div className="loan-processing">
            <div className="loan-spinner" />
            <div className="loan-processing__text">Redirecting to Aadhaar eSign Portal...</div>
            <div className="loan-processing__subtext">
              You will complete the eSign process on the certified NSDL gateway and be returned here automatically.
            </div>
          </div>
        </div>
      </>
    );
  }

  // ─── Cancelled Phase (returned from eSign cancelled) ──────
  if (phase === 'cancelled') {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number" style={{ background: '#f59e0b' }}>!</div>
          <span className="loan-step-text">Step 4 of 5 — Aadhaar eSign</span>
        </div>
        <div className="loan-card">
          <div className="loan-api-tag loan-api-tag--esign">
            ⚠️ eSign — Cancelled
          </div>
          <h2 className="loan-heading" style={{ fontSize: 22 }}>
            Signing Was Cancelled
          </h2>
          <p className="loan-subheading" style={{ marginBottom: 16 }}>
            The eSign process was cancelled at the NSDL gateway. You can try again by clicking below.
          </p>
          <div style={{ marginTop: 20 }}>
            <button
              className="loan-btn loan-btn--primary"
              onClick={() => {
                hasCalledApi.current = false;
                setPhase('consent');
              }}
            >
              ↻ Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  // ─── Error Phase ──────────────────────────────────────────
  if (phase === 'error') {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number" style={{ background: 'var(--loan-error)' }}>!</div>
          <span className="loan-step-text">Step 4 of 5 — Aadhaar eSign</span>
        </div>
        <div className="loan-card">
          <div className="loan-api-tag loan-api-tag--esign">
            ✕ eSign API — Error
          </div>
          <h2 className="loan-heading" style={{ fontSize: 22 }}>
            eSign Request Failed
          </h2>
          <p className="loan-subheading" style={{ marginBottom: 16 }}>
            The eSign service encountered an error. Please verify your configuration and try again.
          </p>
          <div className="loan-doc-preview" style={{ textAlign: 'center', padding: 16 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--loan-error)', wordBreak: 'break-all' }}>
              {error}
            </p>
          </div>
          <div style={{ marginTop: 24 }}>
            <button
              className="loan-btn loan-btn--primary"
              onClick={() => {
                hasCalledApi.current = false;
                setPhase('consent');
                setError('');
              }}
            >
              ↻ Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  // ─── Signed Phase (fallback for sandbox/no-redirect) ──────
  return (
    <>
      <div className="loan-step-label">
        <div className="loan-step-number" style={{ background: 'var(--loan-success)' }}>✓</div>
        <span className="loan-step-text">Step 4 of 5 — Aadhaar eSign</span>
      </div>
      <div className="loan-card">
        <div className="loan-api-tag loan-api-tag--esign">
          ✓ Aadhaar eSign — Sent for Signing
        </div>
        <h2 className="loan-heading" style={{ fontSize: 22 }}>
          Document Sent for Signing
        </h2>
        <p className="loan-subheading" style={{ marginBottom: 16 }}>
          Your document has been submitted to the eSign gateway. The signer will receive notification at their email.
        </p>

        <div className="loan-doc-preview" style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: 16 }}>
            <div className="loan-stamp-badge loan-stamp-badge--esign">
              ✦ SUBMITTED FOR AADHAAR eSIGN
            </div>
          </div>
          <div className="loan-doc-field">
            <span className="loan-doc-field__label">Signer</span>
            <span className="loan-doc-field__value">{signerName}</span>
          </div>
          <div className="loan-doc-field">
            <span className="loan-doc-field__label">Envelope ID</span>
            <span className="loan-doc-field__value" style={{ fontFamily: 'monospace', fontSize: 11 }}>
              {demoState.envelopeId || 'Pending'}
            </span>
          </div>
          <div className="loan-doc-field" style={{ borderBottom: 'none' }}>
            <span className="loan-doc-field__label">Submitted At</span>
            <span className="loan-doc-field__value">
              {new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </span>
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <button className="loan-btn loan-btn--success" onClick={onNext}>
            Complete Onboarding →
          </button>
        </div>
      </div>
    </>
  );
};

export default Step4ESign;
